import type { Subscriber, Updater, Unsubscriber, Writable } from 'svelte/store';
import { get } from 'svelte/store';
import { writable } from 'svelte/store';
import type { getMicrophonePermissionState } from '../../uTheory.Notation/pitch-detection/microphonePermissionCheck';
import Note from '../../uTheory.Notation/note';
import { pierIntervalTimeSeconds, pierOnScreenSeconds } from './pitcyFishConstants';

export type PitchyFishGameState =
  | 'notStarted'
  | 'awaitingMicrophonePermission'
  | 'microphonePermissionDenied'
  | 'awaitingPitchDetectionInit'
  | 'running'
  | 'gameOver'
  | 'errored';

type PitchyFishEvent =
  | 'MichrophonePermissionCheckRequest'
  | 'AwaitingMicrophonePermissionCheck'
  | 'StartPitchDetectionRequest'
  | 'GameStarted';

type PitchyFishEventCallback = (event: PitchyFishEvent) => void;

type TransitionCallback = (state: PitchyFishGameState) => void;

type MicrophonePermissionStatus =
  | 'unchecked'
  | 'checking'
  | 'userRequested'
  | Awaited<ReturnType<typeof getMicrophonePermissionState>>;

export interface PitchyFishGameStateData {
  score: number;
  highScore: number;
  state: PitchyFishGameState;
  scrollTimeSeconds: number;
  pierIntervalTimeSeconds: number;
  /** The lowest in-game-board-range note */
  lowestMidi: number;
  /** The highest in-game-board-range note */
  highestMidi: number;
  error: string;
  microphonePermissionStatus: MicrophonePermissionStatus;
  pitchDetectionStatus: 'notRunning' | 'running';
}

const defaultLowestMidi = new Note('C3').getMidiNumber();
const defaultHighestMidi = new Note('C4').getMidiNumber();

export class PitchyFishGameStore {
  subscribe: (this: void, run: Subscriber<PitchyFishGameStateData>) => Unsubscriber;

  private update: (this: void, updater: Updater<PitchyFishGameStateData>) => void;

  private writable: Writable<PitchyFishGameStateData>;

  constructor({
    highScore = 0,
    lowestMidi = defaultLowestMidi,
    highestMidi = defaultHighestMidi,
  }: {
    highScore?: number;
    lowestMidi?: number;
    highestMidi?: number;
  }) {
    this.writable = writable<PitchyFishGameStateData>({
      score: 0,
      highScore,
      state: 'notStarted',
      scrollTimeSeconds: pierOnScreenSeconds,
      pierIntervalTimeSeconds,
      lowestMidi,
      highestMidi,
      error: '',
      microphonePermissionStatus: 'unchecked',
      pitchDetectionStatus: 'notRunning',
    });
    this.subscribe = this.writable.subscribe;
    this.update = this.writable.update;
  }

  incrementScore() {
    this.update((data) => ({ ...data, score: data.score + 1 }));
  }

  set score(score: number) {
    this.update((data) => ({ ...data, score }));
  }

  set highScore(highScore: number) {
    this.update((data) => ({ ...data, highScore }));
  }

  set state(state: PitchyFishGameState) {
    this.update((data) => ({ ...data, state }));
    this.transitionCallbacks.forEach((cb) => cb(state));
  }

  set error(errorText: string) {
    this.update((data) => ({ ...data, error: errorText }));
    this.state = 'errored';
  }

  set microphonePermissionStatus(status: MicrophonePermissionStatus) {
    this.update((data) => ({ ...data, microphonePermissionStatus: status }));
  }

  set pitchDetectionStatus(status: PitchyFishGameStateData['pitchDetectionStatus']) {
    this.update((data) => ({ ...data, pitchDetectionStatus: status }));
  }

  set lowestMidi(midi: number) {
    this.update((data) => ({ ...data, lowestMidi: midi }));
  }

  set highestMidi(midi: number) {
    this.update((data) => ({ ...data, highestMidi: midi }));
  }

  private transitionCallbacks: TransitionCallback[] = [];

  private eventCallbacks: { eventName: PitchyFishEvent; callback: PitchyFishEventCallback }[] = [];

  addEventListener(event: PitchyFishEvent, callback: PitchyFishEventCallback): void;
  addEventListener(event: 'transition', callback: TransitionCallback): void;
  addEventListener<T extends 'transition' | PitchyFishEvent>(
    event: T,
    callback: T extends 'transition' ? TransitionCallback : PitchyFishEventCallback
  ): void {
    if (event === 'transition') {
      this.transitionCallbacks.push(callback as TransitionCallback);
    } else {
      this.eventCallbacks.push({ eventName: event, callback: callback as PitchyFishEventCallback });
    }
  }

  removeEventListener(event: 'transition', callback: TransitionCallback) {
    this.transitionCallbacks = this.transitionCallbacks.filter((cb) => cb !== callback);
  }

  private emit(event: PitchyFishEvent) {
    this.eventCallbacks.forEach(({ eventName, callback }) => {
      if (eventName === event) callback(event);
    });
  }

  startGame() {
    const state = get(this.writable);
    if (state.pitchDetectionStatus === 'running') {
      this.state = 'running';
      this.emit('GameStarted');
      return;
    }

    switch (state.microphonePermissionStatus) {
      case 'unchecked':
        this.emit('MichrophonePermissionCheckRequest');
        break;
      case 'checking':
        this.emit('AwaitingMicrophonePermissionCheck');
        break;
      case 'unknown':
      case 'prompt':
        this.state = 'awaitingMicrophonePermission';
        break;
      case 'granted':
      case 'userRequested':
        this.state = 'awaitingPitchDetectionInit';
        this.emit('StartPitchDetectionRequest');
        break;
      case 'denied':
        this.state = 'microphonePermissionDenied';
        break;
      default:
        this.error = 'Invalid microphone permission state.';
        this.state = 'errored';
    }
  }
}
